import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, X } from 'lucide-react';
import {
  addSort,
  moveSort,
  removeSort,
  setSortColumn,
  setSortDir,
} from '../../reporting/definitionUpdates';
import type { ReportDefinition } from '../../types/reporting';

interface SortEditorProps {
  definition: ReportDefinition;
  // Result columns that can be an ORDER BY target (dimension/measure aliases for
  // grouped/pivot views, detail column names otherwise). The Wire step computes
  // this from the compiled result so the sidebar and the table headers agree.
  sortableColumns: string[];
  columnLabel: (col: string) => string;
  onChange: (definition: ReportDefinition) => void;
}

// Sidebar "Sort" section mirroring FiltersEditor: an ORDERED list of bordered
// rows (one per definition.sort key) plus a "+ Add sort" control. The row order
// IS the ORDER BY precedence (primary, secondary, …). Every control routes
// through the pure definitionUpdates helpers, and the table-header sort cycle
// mutates the SAME definition.sort, so the two stay in lockstep automatically.
// DuckDB owns the actual ordering via the compiled ORDER BY — nothing sorts in
// JavaScript.
const SortEditor = ({
  definition,
  sortableColumns,
  columnLabel,
  onChange,
}: SortEditorProps) => {
  const sort = definition.sort;
  // Columns not yet used by any sort row — the "+ Add sort" menu, and each row's
  // own column select excludes the OTHER rows' columns so a duplicate ORDER BY
  // target can never be picked (setSortColumn's collision guard backs this up).
  const usedColumns = new Set(sort.map(s => s.by));
  const unusedColumns = sortableColumns.filter(c => !usedColumns.has(c));

  return (
    <div className='space-y-2'>
      {sort.map((key, index) => {
        // A row may pick its own column plus any column no other row uses.
        const rowColumns = sortableColumns.filter(
          c => c === key.by || !usedColumns.has(c)
        );
        const label = columnLabel(key.by);
        return (
          <div key={index} className='space-y-1 rounded-md border p-2'>
            {/* Column select on its own full-width row so long labels aren't
                truncated in the narrow sidebar. */}
            <Select
              value={key.by}
              onValueChange={value =>
                onChange(setSortColumn(definition, index, value))
              }
            >
              <SelectTrigger selectSize='sm' className='flex-1' title={label}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rowColumns.map(col => (
                  <SelectItem key={col} value={col}>
                    {columnLabel(col)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className='flex items-center gap-1'>
              {/* Direction toggle: flip asc/desc in place. */}
              <Button
                size='sm'
                variant='outline'
                className='flex-1'
                aria-label={`${label}: sorted ${
                  key.dir === 'asc' ? 'ascending' : 'descending'
                }`}
                onClick={() =>
                  onChange(
                    setSortDir(
                      definition,
                      index,
                      key.dir === 'asc' ? 'desc' : 'asc'
                    )
                  )
                }
              >
                {key.dir === 'asc' ? (
                  <ArrowUp className='h-4 w-4' />
                ) : (
                  <ArrowDown className='h-4 w-4' />
                )}
                {key.dir === 'asc' ? 'Asc' : 'Desc'}
              </Button>
              {/* Reorder precedence — disabled at the ends. */}
              <Button
                size='sm'
                variant='ghost'
                aria-label={`Move ${label} up`}
                disabled={index === 0}
                onClick={() => onChange(moveSort(definition, index, index - 1))}
              >
                <ChevronUp className='h-4 w-4' />
              </Button>
              <Button
                size='sm'
                variant='ghost'
                aria-label={`Move ${label} down`}
                disabled={index === sort.length - 1}
                onClick={() => onChange(moveSort(definition, index, index + 1))}
              >
                <ChevronDown className='h-4 w-4' />
              </Button>
              <Button
                size='sm'
                variant='ghost'
                aria-label={`Remove ${label} sort`}
                onClick={() => onChange(removeSort(definition, index))}
              >
                <X className='h-4 w-4' />
              </Button>
            </div>
          </div>
        );
      })}
      {/* Hidden once every sortable column is already used. */}
      {unusedColumns.length > 0 && (
        <Select
          value=''
          onValueChange={value => onChange(addSort(definition, value))}
        >
          <SelectTrigger selectSize='sm'>
            <SelectValue placeholder='+ Add sort' />
          </SelectTrigger>
          <SelectContent>
            {unusedColumns.map(col => (
              <SelectItem key={col} value={col}>
                {columnLabel(col)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
};

export type { SortEditorProps };
export default SortEditor;
